import React, { useState, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Grid,
  Box,
  makeStyles,
  Typography,
  IconButton,
  CircularProgress,
} from 'vendor/material';
import { ChevronDown } from 'mdi-material-ui';
import { ampli } from 'utils/analytics/ampli';
import { INTEGRATIONS } from '../../../constants';
import {
  useAwsVendorEnrollInSddsFormSectionQuery,
  useUpdateAwsVendorEnrollInSddsFormSectionMutation,
} from 'generated/graphql';
import PermissionTimeSubTitle from '../../../../../components/PermissionTimeSubTitle/PermissionTimeSubTitle';
import { Link, Banner, Button, TextField } from '@tackle-io/platform-ui';
import { useToast } from 'hooks';
import { useAwsCredentialsPolling } from '../../../../../hooks/useAwsCredentialsPolling';

const useStyles = makeStyles((theme) => ({
  headerContainer: {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    '&:hover': {
      opacity: 0.8,
    },
  },
  headerText: {
    color: theme.palette.BLUE400,
    marginLeft: theme.spacing(2),
    marginTop: theme.spacing(3),
    fontWeight: 600,
    fontSize: theme.typography.pxToRem(20),
  },
  chevron: {
    transform: 'rotate(0deg)',
    transition: theme.transitions.create('transform', {
      duration: theme.transitions.duration.shortest,
    }),
    '&.expanded': {
      transform: 'rotate(180deg)',
    },
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    padding: theme.spacing(2),
    marginRight: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  subHeaderText: {
    fontSize: theme.typography.pxToRem(16),
    color: theme.palette.NEUTRAL700,
  },
  contentContainer: {
    marginLeft: theme.spacing(2),
    marginRight: theme.spacing(4),
    marginTop: theme.spacing(2),
  },
}));

interface SDDSEnrollmentCardProps {
  isConnectButtonClicked: boolean;
}

const SDDS_ENROLL = 'sdds-enroll';

const SDDSEnrollmentCard: React.FC<SDDSEnrollmentCardProps> = ({
  isConnectButtonClicked,
}) => {
  const { credentials, hasStep1Complete } = useAwsCredentialsPolling();
  const history = useHistory();
  const [isExpanded, setIsExpanded] = useState(false);

  const classes = useStyles();

  const { data, loading } = useAwsVendorEnrollInSddsFormSectionQuery({
    context: { batchKey: 'vendor-onboarding' },
    fetchPolicy: 'cache-and-network', // Always fetch from network but use cache if available
  });

  const [updateValues] = useUpdateAwsVendorEnrollInSddsFormSectionMutation();
  const stacks = useMemo(
    () => data?.currentUser?.vendor?.onboarding?.aws?.stacks || [],
    [data],
  );
  // Automatically expand/collapse card based on step 1 completion status
  React.useEffect(() => {
    // Expand the card if step 1 is complete, otherwise collapse it
    setIsExpanded(hasStep1Complete);
  }, [hasStep1Complete, isConnectButtonClicked]);

  const { toaster } = useToast();

  const handleFinishConnection = async () => {
    try {
      // Update stacks if needed
      if (!stacks.includes(SDDS_ENROLL) && data?.currentUser?.vendor?.id) {
        await updateValues({
          variables: {
            updates: {
              id: data.currentUser.vendor.id,
              onboarding: {
                aws: {
                  stacks: [...stacks, SDDS_ENROLL],
                },
              },
            },
          },
        });
      }

      ampli.buttonClicked({
        button_location: 'integrations-connect-to-aws',
        button_name: 'finish-sdds-enrollment-button',
        button_product_area: INTEGRATIONS,
      });

      history.replace({
        pathname: '/integrations',
        search: '?toast=sdds_success',
      });
    } catch (error) {
      console.error('Error updating stacks:', error);
      toaster({
        message: 'Failed to update AWS connection status',
        options: {
          position: 'top-center',
          type: 'error',
          autoClose: 4000,
          draggable: false,
        },
      });
    }
  };

  const hasSDDSKeys = !!(
    credentials.s3Arn &&
    credentials.kmsKeyArn &&
    credentials.snsTopicArn
  );

  // Show a loading state with a spinner or don't render at all until data is available
  if (
    loading ||
    !data ||
    !data.currentUser ||
    !data.currentUser.vendor ||
    !data.currentUser.vendor.onboarding
  ) {
    return (
      <Box
        data-testid="sdds-enrollment-card"
        sx={{
          minHeight: '100px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <Box data-testid="sdds-enrollment-card">
      <Box
        className={classes.headerContainer}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <Box flex={1} mb={2}>
          <Typography
            className={classes.headerText}
            style={{
              color: stacks.includes(SDDS_ENROLL)
                ? '#00875A'
                : isExpanded
                ? '#253858'
                : '#0052CC',
            }}
          >
            Step 2: Enroll in SDDS to activate your AWS connection
          </Typography>
          <Box ml={2} mt={1}>
            <PermissionTimeSubTitle
              permissions="AWS Admin"
              timeToComplete="5 minutes"
            />
          </Box>
        </Box>
        <IconButton size="small">
          <ChevronDown
            className={`${classes.chevron} ${isExpanded ? 'expanded' : ''}`}
          />
        </IconButton>
      </Box>

      {isExpanded && !loading && (
        <>
          <Box className={classes.contentContainer}>
            <Grid container direction="column" spacing={2}>
              {!hasSDDSKeys && (
                <Grid item>
                  <Banner
                    title="Connecting in Step 1 is required before enrolling in SDDS."
                    borderPosition="top"
                  />
                </Grid>
              )}
              {!stacks.includes(SDDS_ENROLL) && (
                <Grid item>
                  <Banner
                    title="Your AWS integration won't work until this is complete"
                    type="warning"
                    borderPosition="top"
                  />
                </Grid>
              )}
              <Grid item>
                <Typography className={classes.subHeaderText}>
                  Follow the steps below to enroll in this free Marketplace
                  service that provides data feeds to power reporting.
                </Typography>
              </Grid>
              <Grid item>
                <Typography className={classes.subHeaderText}>
                  1. Navigate to the{' '}
                  <Link
                    showExternalIcon
                    to="https://aws.amazon.com/marketplace/management/reports/data-feed-configuration"
                    external
                  >
                    Marketplace Management Data Feed Configuration Form
                  </Link>
                </Typography>
              </Grid>
              <Grid item>
                <Typography className={classes.subHeaderText}>
                  2. Copy and paste the values from the fields below to complete
                  enrollment.
                </Typography>
              </Grid>
              <Grid item>
                <Typography className={classes.subHeaderText}>
                  3. Click Submit.
                </Typography>
              </Grid>
              <Grid item>
                <Box mt={1}>
                  <TextField
                    label="S3 ARN"
                    name="s3Arn"
                    id="s3-arn"
                    disabled={!credentials.s3Arn}
                    withCopyToClipboard={!!credentials.s3Arn}
                    value={credentials.s3Arn || ''}
                    valueAccessor={() => credentials.s3Arn || ''}
                  />
                </Box>
              </Grid>
              <Grid item>
                <TextField
                  label="KMS key ARN"
                  name="kmsKeyArn"
                  id="kms-key-arn"
                  disabled={!credentials.kmsKeyArn}
                  withCopyToClipboard={!!credentials.kmsKeyArn}
                  value={credentials.kmsKeyArn || ''}
                  valueAccessor={() => credentials.kmsKeyArn || ''}
                />
              </Grid>
              <Grid item>
                <TextField
                  label="SNS topic ARN"
                  name="snsTopicArn"
                  id="sns-topic-arn"
                  disabled={!credentials.snsTopicArn}
                  withCopyToClipboard={!!credentials.snsTopicArn}
                  value={credentials.snsTopicArn || ''}
                  valueAccessor={() => credentials.snsTopicArn || ''}
                />
              </Grid>
            </Grid>
          </Box>
          <Box className={classes.buttonContainer}>
            <Button
              appearance="primary"
              onClick={handleFinishConnection}
              disabled={!credentials.s3Arn || stacks.includes(SDDS_ENROLL)}
              data-testid="finish-sdds-enrollment-button"
            >
              Finish connection
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
};

export default SDDSEnrollmentCard;
